import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Send, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const FIELDS = [
  { k: "name", l: "Nome scuola", placeholder: "Es. Scuola dell'Infanzia Don Bosco" },
  { k: "address", l: "Indirizzo", placeholder: "Via, numero civico" },
  { k: "city", l: "Comune" },
  { k: "province", l: "Provincia (sigla)", maxLength: 4 },
  { k: "postal_code", l: "CAP" },
  { k: "phone", l: "Telefono" },
  { k: "email", l: "Email" },
  { k: "pec", l: "PEC" },
  { k: "website", l: "Sito web" },
];

export default function CorrectSchool() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [vals, setVals] = useState({});
  const [notes, setNotes] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = "Segnala una correzione — Per manina";
    api.get(`/schools/${id}`).then(({ data }) => {
      setSchool(data);
      const init = {};
      FIELDS.forEach((f) => { init[f.k] = data[f.k] || ""; });
      setVals(init);
    }).catch(() => toast.error("Scuola non trovata"));
  }, [id]);

  useEffect(() => {
    if (user) {
      setContactEmail((e) => e || user.email);
      setContactName((n) => n || user.name);
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) { toast.error("Devi accettare la Privacy Policy"); return; }
    if (!contactEmail) { toast.error("Indica un'email di contatto"); return; }

    const proposals = {};
    FIELDS.forEach((f) => {
      const newVal = (vals[f.k] || "").trim();
      const oldVal = (school[f.k] || "").trim();
      if (newVal && newVal !== oldVal) proposals[f.k] = newVal;
    });
    if (Object.keys(proposals).length === 0) {
      toast.error("Non hai modificato nessun campo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/schools/${id}/correction`, {
        proposals, notes, contact_email: contactEmail, contact_name: contactName, privacy_consent: true,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20">
        <div className="card-surface p-10 text-center" data-testid="correction-success">
          <CheckCircle2 className="mx-auto text-[#1A362D]" size={48} />
          <h1 className="mt-4 font-display text-3xl text-[#1A362D]">Grazie per la correzione!</h1>
          <p className="mt-3 font-ui text-[#5C6B64] leading-relaxed">
            La tua segnalazione è arrivata. La verifichiamo e aggiorniamo la scheda al più presto.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <button onClick={() => navigate(`/scuola/${id}`)} className="btn-primary text-sm">Torna alla scheda</button>
          </div>
        </div>
      </div>
    );
  }

  if (!school) {
    return <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="correct-school-page">
      <Link to={`/scuola/${id}`} className="inline-flex items-center gap-1 text-sm font-ui text-[#C05C45] hover:underline mb-4"><ArrowLeft size={14} /> Torna alla scheda</Link>
      <p className="label-eyebrow text-[#C05C45]">Correzione dati</p>
      <h1 className="mt-2 font-display text-4xl tracking-tight text-[#1A362D]">Segnala una correzione</h1>
      <p className="mt-3 font-ui text-[#5C6B64]">Stai segnalando una correzione per: <span className="text-[#1A362D] font-medium">{school.name}</span> — {school.city} ({school.province})</p>

      <p className="mt-4 font-ui text-sm text-[#5C6B64] leading-relaxed">
        Modifica solo i campi che vuoi correggere. Verifichiamo prima di applicare le modifiche.
      </p>

      <form onSubmit={submit} className="mt-8 card-surface p-6 sm:p-8 space-y-5" data-testid="correction-form">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="block text-sm font-ui text-[#1A362D] mb-1">{f.l}</label>
            <input
              value={vals[f.k] || ""}
              onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })}
              data-testid={`correction-field-${f.k}`}
              maxLength={f.maxLength}
              placeholder={f.placeholder}
              className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none"
            />
            {school[f.k] && vals[f.k] !== school[f.k] && (
              <p className="mt-1 text-xs font-ui text-[#8A9691]">Attuale: <span className="line-through">{school[f.k]}</span></p>
            )}
          </div>
        ))}

        <div>
          <label className="block text-sm font-ui text-[#1A362D] mb-1">Note libere</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="correction-notes"
            placeholder="Spiegaci da dove arriva l'informazione (es. sito web, sopralluogo, telefonata in segreteria…)"
            className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none"
          />
        </div>

        <div className="divider pt-5">
          <p className="label-eyebrow text-[#1A362D] mb-3">I tuoi contatti</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Il tuo nome" data-testid="correction-contact-name" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
            <input required type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="La tua email *" data-testid="correction-contact-email" className="w-full border border-[#E8E6E1] rounded-md px-3 py-2 font-ui text-sm bg-white focus:border-[#C05C45] focus:outline-none" />
          </div>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="correction-privacy" className="mt-1 accent-[#C05C45]" />
          <span className="font-ui text-xs text-[#5C6B64] leading-relaxed">
            Accetto il trattamento dei dati come da <a href="/info/privacy" target="_blank" rel="noreferrer" className="underline text-[#C05C45]">Privacy Policy</a>.
          </span>
        </label>

        <div className="flex gap-2">
          <button type="submit" disabled={submitting} data-testid="submit-correction" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Send size={14} /> {submitting ? "Invio…" : "Invia correzione"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary text-sm">Annulla</button>
        </div>
      </form>
    </div>
  );
}
